import { getCachedComputedStyle } from "./getCachedComputedStyle";

class GetStyleProperty {
  static instance: GetStyleProperty | null;
  constructor() {
    // Prevent direct instantiation
    if (GetStyleProperty.instance) {
      throw new Error(
        "GetStyleProperty is a singleton. Use getStyleProperty() function instead.",
      );
    }
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): GetStyleProperty {
    if (!GetStyleProperty.instance) {
      GetStyleProperty.instance = new GetStyleProperty();
    }
    return GetStyleProperty.instance;
  }

  /**
   * Get a specific CSS property value, checking inline styles first, then computed styles
   * @param element - The element to get the style property from
   * @param property - The CSS property name (camelCase or kebab-case)
   * @param skipCache - Whether to skip the computed style cache (default: false)
   */
  getStyleProperty(
    element: HTMLElement,
    property: string,
    skipCache: boolean = false,
  ): string | undefined {
    if (!element || typeof element !== "object" || !element.nodeType) {
      throw new TypeError(
        'First argument must be an Element.\nExample: getStyleProperty(document.getElementById("myElement"), "backgroundColor")',
      );
    }

    if (!property || typeof property !== "string") {
      throw new TypeError(
        'Second argument must be a CSS property name string.\nExample: "backgroundColor", "fontSize", "marginTop"',
      );
    }

    if (typeof skipCache !== "boolean") {
      throw new TypeError(
        'Third argument (skipCache) must be a boolean.\nExample: getStyleProperty(element, "color", true)',
      );
    }

    // Normalize property name (convert kebab-case to camelCase if needed)
    const normalizedProperty = this.normalizePropertyName(property);

    // STEP 1: Check inline styles first (highest specificity, fastest)
    const inlineValue = (element.style as unknown as Record<string, string>)[
      normalizedProperty
    ];
    if (inlineValue) {
      return inlineValue;
    }

    // STEP 2: Fall back to computed styles using our cached function
    // This is the expensive operation, but it's cached by getCachedComputedStyle
    if (typeof getCachedComputedStyle === "undefined") {
      throw new Error(
        "getCachedComputedStyle function not available. Please import it from the \n\n\`import { getCachedComputedStyle } from './getCachedComputedStyle.js';\`\n\n module.",
      );
    }

    const computedStyles = getCachedComputedStyle(element, null, skipCache);
    return (
      (computedStyles as Record<string, string>)[normalizedProperty] ||
      undefined
    );
  }

  /**
   * Normalize CSS property names (convert kebab-case to camelCase)
   */
  private normalizePropertyName(property: string): string {
    // Convert kebab-case to camelCase (e.g., 'background-color' -> 'backgroundColor')
    return property.replace(/-([a-z])/g, (match, letter) =>
      letter.toUpperCase(),
    );
  }
}

// Initialize static property outside the class (ES5-compatible)
GetStyleProperty.instance = null;

// Get singleton instance and bind the method
const instance = GetStyleProperty.getInstance();
const getStyleProperty = instance.getStyleProperty.bind(instance);

// Export both the class (for advanced usage) and the function (for normal usage)
export { GetStyleProperty, getStyleProperty };

// Usage examples:
/*
// Import both singletons
import { getCachedComputedStyle } from './cached-computed-style.js';
import { getStyleProperty } from './style-property.js';

// PRIMARY USAGE - Get specific CSS property values efficiently
const element = document.getElementById('myElement');

// These check inline styles first (instant), then use cached computed styles
const backgroundColor = getStyleProperty(element, 'backgroundColor');
const fontSize = getStyleProperty(element, 'fontSize');
const marginTop = getStyleProperty(element, 'margin-top'); // Auto-converts to camelCase

// Skip cache for fresh computed values
const freshColor = getStyleProperty(element, 'color', true);

// PERFORMANCE COMPARISON:
// Old way - always queries computed styles (expensive)
const oldColor = getComputedStyle(element).color;
const oldBg = getComputedStyle(element).backgroundColor; // Another expensive call!

// New way - inline check + cached computed styles (fast)
const newColor = getStyleProperty(element, 'color');        // Fast: inline or cached
const newBg = getStyleProperty(element, 'backgroundColor'); // Fast: reuses cached computedStyle

// PRACTICAL EXAMPLES:

// 1. Animation system - needs current values
function animateToColor(element, targetColor) {
  const currentColor = getStyleProperty(element, 'color');
  // Animate from currentColor to targetColor
}

// 2. Responsive design - check effective styles
function adjustLayout(element) {
  const width = getStyleProperty(element, 'width');
  const display = getStyleProperty(element, 'display');

  if (parseInt(width) < 768) {
    // Mobile layout adjustments
  }
}

// 3. Theme detection
function getCurrentTheme(element) {
  const bgColor = getStyleProperty(element, 'backgroundColor');
  const textColor = getStyleProperty(element, 'color');

  return {
    isDark: bgColor.includes('rgb(0') || bgColor.includes('#000'),
    colors: { background: bgColor, text: textColor }
  };
}

// WHY THIS ARCHITECTURE WORKS:
// - Inline styles: Instant access (no DOM query needed)
// - Computed styles: Expensive query, but cached by getCachedComputedStyle
// - Property access: Fast property lookup from cached CSSStyleDeclaration
// - No double caching: Only cache where it matters (the expensive part)
*/
